import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { 
  Filter, 
  Download, 
  FileText, 
  BarChart3, 
  Settings, 
  Eye,
  RefreshCw,
  Calendar,
  Search,
  MoreHorizontal
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export interface ChartFilterOptions {
  timeRange?: string;
  category?: string;
  scope?: string;
  dataSource?: string;
  chartType?: string;
  tags?: string[];
  customDateRange?: {
    from: Date | undefined;
    to: Date | undefined;
  };
  customFilters?: Record<string, any>;
}

export interface ChartExportOptions {
  format: 'png' | 'pdf' | 'csv';
  includeData?: boolean;
  includeMetadata?: boolean;
  customTitle?: string;
}

interface IndividualChartControlsProps {
  chartId: string;
  chartTitle: string;
  chartType: 'bar' | 'line' | 'pie' | 'area' | 'mixed';
  filters: ChartFilterOptions;
  onFiltersChange: (filters: ChartFilterOptions) => void;
  onExport: (options: ChartExportOptions) => void;
  onRefresh: () => void;
  onViewDetails?: () => void;
  availableFilters?: {
    timeRanges?: string[];
    categories?: string[];
    scopes?: string[];
    dataSources?: string[];
    chartTypes?: string[];
    tags?: string[];
    customDateRange?: {
      from: Date | undefined;
      to: Date | undefined;
    };
  };
  className?: string;
  showAdvancedFilters?: boolean;
}

export const IndividualChartControls: React.FC<IndividualChartControlsProps> = ({
  chartId,
  chartTitle,
  chartType,
  filters,
  onFiltersChange,
  onExport,
  onRefresh,
  onViewDetails,
  availableFilters = {},
  className = '',
  showAdvancedFilters = false
}) => {
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [customFilters, setCustomFilters] = useState<Record<string, any>>({});

  const defaultTimeRanges = ['3months', '6months', '1year', '2years', 'custom', 'all'];
  const defaultCategories = ['all', 'carbon', 'energy', 'water', 'materials', 'esg'];
  const defaultScopes = ['all', 'Scope 1', 'Scope 2', 'Scope 3'];
  const defaultDataSources = ['all', 'Internal', 'Supplier', 'Audit', 'Manual'];
  const defaultChartTypes = ['all', 'bar', 'line', 'pie', 'area', 'mixed'];

  const timeRanges = availableFilters.timeRanges || defaultTimeRanges;
  const categories = availableFilters.categories || defaultCategories;
  const scopes = availableFilters.scopes || defaultScopes;
  const dataSources = availableFilters.dataSources || defaultDataSources;
  const chartTypes = availableFilters.chartTypes || defaultChartTypes;

  const handleFilterChange = (key: keyof ChartFilterOptions, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleCustomFilterChange = (key: string, value: any) => {
    const newCustomFilters = { ...customFilters, [key]: value };
    setCustomFilters(newCustomFilters);
    onFiltersChange({
      ...filters,
      customFilters: newCustomFilters
    });
  };

  const handleExport = (format: ChartExportOptions['format']) => {
    const exportOptions: ChartExportOptions = {
      format,
      includeData: true,
      includeMetadata: true,
      customTitle: `${chartTitle} - ${new Date().toLocaleDateString()}`
    };

    onExport(exportOptions);
    
    toast({
      title: 'Export Started',
      description: `Exporting ${chartTitle} as ${format.toUpperCase()}`,
    });
  };

  const handleQuickExport = () => {
    handleExport('png');
  };

  const getChartTypeIcon = (type: string) => {
    switch (type) {
      case 'bar': return <BarChart3 className="h-4 w-4" />;
      case 'line': return <BarChart3 className="h-4 w-4" />;
      case 'pie': return <BarChart3 className="h-4 w-4" />;
      case 'area': return <BarChart3 className="h-4 w-4" />;
      default: return <BarChart3 className="h-4 w-4" />;
    }
  };

  const getChartTypeColor = (type: string) => {
    switch (type) {
      case 'bar': return 'bg-blue-100 text-blue-800';
      case 'line': return 'bg-green-100 text-green-800';
      case 'pie': return 'bg-purple-100 text-purple-800';
      case 'area': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Basic Controls Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={getChartTypeColor(chartType)}>
            {getChartTypeIcon(chartType)}
            <span className="ml-1 capitalize">{chartType}</span>
          </Badge>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            className="h-8 w-8 p-0"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {/* View Details Button */}
          {onViewDetails && (
            <Button
              variant="outline"
              size="sm"
              onClick={onViewDetails}
              className="h-8 px-3"
            >
              <Eye className="h-4 w-4 mr-1" />
              View Details
            </Button>
          )}

          {/* Quick Export */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleQuickExport}
            className="h-8 px-3"
          >
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>

          {/* Export Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => handleExport('png')}>
                <FileText className="h-4 w-4 mr-2" />
                Export as PNG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pdf')}>
                <FileText className="h-4 w-4 mr-2" />
                Export as PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                <FileText className="h-4 w-4 mr-2" />
                Export as CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Expand/Collapse Filters */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-8 w-8 p-0"
          >
            <Filter className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Expanded Filters */}
      {isExpanded && (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg border">
          <div className="flex items-center gap-2 mb-3">
            <Settings className="h-4 w-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Chart Filters</span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {/* Time Range Filter */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Time Range</Label>
              <Select 
                value={filters.timeRange || '1year'} 
                onValueChange={(value) => handleFilterChange('timeRange', value)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timeRanges.map(range => (
                    <SelectItem key={range} value={range}>
                      {range === '3months' ? '3 Months' :
                       range === '6months' ? '6 Months' :
                       range === '1year' ? '1 Year' :
                       range === '2years' ? '2 Years' :
                       range === 'all' ? 'All Time' : range}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category Filter */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Category</Label>
              <Select 
                value={filters.category || 'all'} 
                onValueChange={(value) => handleFilterChange('category', value)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(category => (
                    <SelectItem key={category} value={category}>
                      {category === 'all' ? 'All Categories' :
                       category.charAt(0).toUpperCase() + category.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Scope Filter */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Scope</Label>
              <Select 
                value={filters.scope || 'all'} 
                onValueChange={(value) => handleFilterChange('scope', value)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {scopes.map(scope => (
                    <SelectItem key={scope} value={scope}>
                      {scope === 'all' ? 'All Scopes' : scope}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Data Source Filter */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Data Source</Label>
              <Select 
                value={filters.dataSource || 'all'} 
                onValueChange={(value) => handleFilterChange('dataSource', value)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {dataSources.map(source => (
                    <SelectItem key={source} value={source}>
                      {source === 'all' ? 'All Sources' : source}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Chart Type Filter */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Chart Type</Label>
              <Select 
                value={filters.chartType || 'all'} 
                onValueChange={(value) => handleFilterChange('chartType', value)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {chartTypes.map(type => (
                    <SelectItem key={type} value={type}>
                      {type === 'all' ? 'All Types' :
                       type.charAt(0).toUpperCase() + type.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Advanced Filters</span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {/* Custom Date Range */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Start Date</Label>
                  <Input
                    type="date"
                    className="h-8 text-xs"
                    onChange={(e) => handleCustomFilterChange('startDate', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-xs font-medium">End Date</Label>
                  <Input
                    type="date"
                    className="h-8 text-xs"
                    onChange={(e) => handleCustomFilterChange('endDate', e.target.value)}
                  />
                </div>

                {/* Search Filter */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                    <Input
                      placeholder="Search data..."
                      className="h-8 text-xs pl-7"
                      onChange={(e) => handleCustomFilterChange('search', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filter Summary */}
          <div className="flex items-center gap-2 pt-2 border-t">
            <span className="text-xs text-gray-600">Active filters:</span>
            {filters.timeRange && filters.timeRange !== '1year' && (
              <Badge variant="secondary" className="text-xs">
                {filters.timeRange}
              </Badge>
            )}
            {filters.category && filters.category !== 'all' && (
              <Badge variant="secondary" className="text-xs">
                {filters.category}
              </Badge>
            )}
            {filters.scope && filters.scope !== 'all' && (
              <Badge variant="secondary" className="text-xs">
                {filters.scope}
              </Badge>
            )}
            {filters.dataSource && filters.dataSource !== 'all' && (
              <Badge variant="secondary" className="text-xs">
                {filters.dataSource}
              </Badge>
            )}
            {filters.chartType && filters.chartType !== 'all' && (
              <Badge variant="secondary" className="text-xs">
                {filters.chartType}
              </Badge>
            )}
            {Object.keys(customFilters).length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {Object.keys(customFilters).length} custom
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
